<template>
    <v-row justify="center">
        <v-dialog v-model="create_event_dialog" max-width="1000" persistent scrollable>
            <v-card v-if="lead && quote">
                <v-card-title class="dialog-header">
                    <h2 class="dialog-title">Book Quote</h2>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text class="pa-6">
                    <v-form ref="event_form_data">
                        <!-- Event Name Section -->
                        <v-row class="mb-4">
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-if="lead.event == null"
                                    v-model="event.name"
                                    label="Enter Event Name *"
                                    dense
                                    outlined
                                    tabindex="7"
                                    required
                                    :rules="[v => !!v || 'You are required to enter the name of this event.']"
                                ></v-text-field>
                                <div v-else class="event-name-display">
                                    Event Name: {{ event.name }}
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Event Details Section -->
                        <v-card flat outlined class="info-card mb-6">
                            <v-card-text>
                                <div class="section-label mb-4">Event Details</div>
                                <v-row>
                                    <v-col cols="6" sm="3" class="info-item">
                                        <div class="info-label">Organiser Name</div>
                                        <div class="info-value">{{ lead.first_name + ' ' + lead.last_name }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="3" class="info-item">
                                        <div class="info-label">Event Date</div>
                                        <div class="info-value">{{ $date(lead.start_date).format('MMMM DD, YYYY') }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="3" class="info-item">
                                        <div class="info-label">Location</div>
                                        <div class="info-value">{{ lead.location ? lead.location.name : 'N/A' }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="3" class="info-item">
                                        <div class="info-label">Group Size</div>
                                        <div class="info-value">{{ lead.people }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <!-- Quote Summary Section -->
                        <v-card flat outlined class="info-card mb-6">
                            <v-card-text>
                                <div class="section-label mb-4">Quote Summary</div>
                                <v-row>
                                    <v-col cols="12" sm="4" class="info-item">
                                        <div class="info-label">Quote Name</div>
                                        <div class="info-value-bold">{{ quote.name }}</div>
                                    </v-col>
                                    <v-col cols="12" sm="4" class="info-item">
                                        <div class="info-label">Quote Price</div>
                                        <div class="info-value-bold">{{ quote.total }}</div>
                                    </v-col>
                                    <v-col cols="12" sm="4" class="info-item">
                                        <div class="info-label">Accommodation</div>
                                        <div
                                            v-for="(build, accom_index) in quote.builds.filter(b => b.type_id == 1 && b.supplier)"
                                            :key="'accom' + accom_index"
                                            class="info-value-bold"
                                        >
                                            {{ build.supplier.supplier_name }}
                                        </div>
                                        <div v-if="!quote.builds.find(b => b.type_id == 1 && b.supplier)" class="info-value">
                                            None
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <!-- Activities Section -->
                        <div class="section-label mb-4">Activities</div>
                        <v-card
                            v-for="(build, index) in quote.builds.filter((x) => x.type_id != 1)"
                            :key="index"
                            flat
                            outlined
                            class="activity-card mb-3"
                        >
                            <v-card-text>
                                <v-row align="center">
                                    <v-col cols="12" md="6">
                                        <div class="supplier-name">
                                            {{ build.supplier ? build.supplier.supplier_name : 'Unknown Supplier' }}
                                        </div>
                                        <div class="activity-details">
                                            <span v-if="build.option">
                                                {{ build.option.name }} ({{ $date(build.start_date).format('dddd') }})
                                            </span>
                                            <span v-else class="text--secondary">
                                                No option selected ({{ $date(build.start_date).format('dddd') }})
                                            </span>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="build.start_time"
                                            label="Time to be there"
                                            dense
                                            outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <div v-if="quote.builds.filter((x) => x.type_id != 1).length === 0" class="no-activities">
                            No activities included in this quote
                        </div>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        elevation="0"
                        outlined
                        @click="closeCreateEventDialog()"
                        class="px-6"
                        tabindex="14"
                    >
                        Close
                    </v-btn>
                    <v-btn
                        elevation="0"
                        color="primary"
                        @click="createEvent()"
                        class="px-6"
                        tabindex="13"
                    >
                        Book
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    props: {
        create_event_dialog: {
            type: Boolean,
            required: true
        },
        quote: {
            type: Object,
            required: true
        },
        lead: {
            type: Object,
            required: true
        }
    },

    data: () => ({
        event: {
            name: null,
            deposit_amount: 0,
        },
    }),

    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
    },

    methods: {
        closeCreateEventDialog() {
            this.$emit('closeCreateEventDialog');
        },

        createEvent() {
            if (this.lead.event == null) {
                const valid = this.$refs.event_form_data.validate();
                if (valid) {
                    this.$emit('createEvent', this.event, this.quote);
                }
            } else {
                this.$emit('bookQuote', this.quote);
            }
        }
    },
};
</script>

<style lang="scss" scoped>
// Dialog Header
.dialog-header {
    background-color: #f5f5f5;
    padding: 20px 24px;
}

.dialog-title {
    font-family: 'Manrope', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

// Event Name Display
.event-name-display {
    font-family: 'Manrope', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #333;
    padding: 12px 0;
}

// Section Labels
.section-label {
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #555;
}

// Info Cards
.info-card {
    background-color: #fafafa;
    border-radius: 4px;
}

.info-item {
    padding: 8px 12px;
}

.info-label {
    font-family: 'Manrope', sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #757575;
    margin-bottom: 4px;
}

.info-value {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #333;
}

.info-value-bold {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #333;
}

// Activity Cards
.activity-card {
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
}

.supplier-name {
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
}

.activity-details {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #666;
}

.no-activities {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-style: italic;
    color: #999;
    text-align: center;
    padding: 24px;
}
</style>